import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  HStack,
  OutlineDangerButton,
  PrimaryButton,
  TertiaryButton,
  VStack,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { resolveFormErrorAlertMessage } from '~/libs/formErrors'
import { parseDateInputValue } from '~/models/civilDate'
import { SingularDayForm } from '~/pages/AdminSingularDay/components/SingularDayForm'
import type { SingularDayRow } from '~/pages/AdminSingularDay/hooks/useListSingularDays'
import { useUpdateSingularDay } from '~/pages/AdminSingularDay/hooks/useUpdateSingularDay'
import { normalizeAreaCodesForSubmit } from '~/pages/AdminSingularDay/models/options'
import {
  type SingularDayFormInput,
  SingularDayFormSchema,
  type SingularDayFormValues,
} from '~/pages/AdminSingularDay/models/schema'
import { mapSingularDaySaveError } from '~/pages/AdminSingularDay/models/serverError'

type Props = {
  isOpen: boolean
  row: SingularDayRow
  onClose: () => void
  onDelete: (row: SingularDayRow) => void
  /** 保存後に同一日付の全エリア共通レコードが検知された時のコールバック (AC-MSD-013a)。 */
  onInfo?: (name: string) => void
  /** 「特異グループ名を編集」 (一括エディタ) を開く。 */
  onEditGroupNames?: () => void
}

/**
 * 特異日編集モーダル (AC-04: 日付は readonly で変更不可、 エリア・名称・
 * カテゴリ・需要扱いを編集可能。 削除ボタンから確認ダイアログへ)。
 *
 * identity は surrogate `id`。 Update は `id` + `expectedUpdatedAt` で行い、
 * date は immutable なので送らない (handler も無視する)。 エリアは未選択 =
 * 全エリア共通、 10 エリア全選択は送信時に空へ正規化する。
 *
 * React Hook Form + Zod で validation。
 */
export const EditSingularDayModal = ({
  isOpen,
  row,
  onClose,
  onDelete,
  onInfo,
  onEditGroupNames,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const showSuccessToast = useSuccessToast()
  const { mutateAsync, isPending } = useUpdateSingularDay()

  const {
    register,
    control,
    handleSubmit,
    reset,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<SingularDayFormInput, unknown, SingularDayFormValues>({
    resolver: zodResolver(SingularDayFormSchema),
    mode: 'onBlur',
    defaultValues: {
      name: row.name,
      category: row.category,
      demandBehavior: row.demandBehavior,
      singularGroup: row.singularGroup,
      areaCodes: row.areaCodes,
    },
  })

  const handleClose = (): void => {
    reset()
    onClose()
  }

  const onSubmit = async (values: SingularDayFormValues): Promise<void> => {
    try {
      const resp = await mutateAsync({
        id: row.id,
        name: values.name,
        category: values.category,
        demandBehavior: values.demandBehavior,
        singularGroup: values.singularGroup,
        areaCodes: normalizeAreaCodesForSubmit(values.areaCodes),
        expectedUpdatedAt: row.updatedAt,
      })
      if (
        resp.overriddenAllAreaName !== undefined &&
        resp.overriddenAllAreaName !== ''
      ) {
        onInfo?.(resp.overriddenAllAreaName)
      }
      reset()
      onClose()
      // 2026-08-28 デザイナー指摘「削除・追加・編集の成功時は全画面共通で toast」。
      showSuccessToast({ title: t('page:adminSingularDay.toast.saved') })
    } catch (err) {
      setError('root.serverError', {
        message: mapSingularDaySaveError(err, t, 'edit'),
      })
    }
  }

  const handleSave = (): void => {
    void handleSubmit(onSubmit, (formErrors) => {
      // 項目脇に FieldErrorText が出るエラーは alert に再掲しない (重複表示になる)。
      const alertMessage = resolveFormErrorAlertMessage(
        formErrors,
        t('page:adminSingularDay.serverError.validation'),
      )
      if (alertMessage !== undefined) {
        setError('root.serverError', { message: alertMessage })
      } else {
        // 直前の失敗で出した alert が残らないよう明示的に消す
        // (項目脇の表示だけに切り替わるため)。
        clearErrors('root.serverError')
      }
    })()
  }

  // Edit モーダルは左に削除導線 (OutlineDangerButton → onDelete で
  // DeleteSingularDayModal を開く、 spec AC-009) を持つ 3 ボタン構成のため、
  // cancel/save のみの共通 SaveCancelFooter では表現できず custom footer を
  // 維持する (EditEsljAccountModal と同型)。
  const footer = (
    <HStack justify="space-between" width="100%">
      <OutlineDangerButton
        onClick={() => {
          onDelete(row)
        }}
      >
        {t('common:action.delete')}
      </OutlineDangerButton>
      <HStack gap={3}>
        <TertiaryButton onClick={handleClose}>
          {t('common:action.cancel')}
        </TertiaryButton>
        <PrimaryButton loading={isPending} onClick={handleSave}>
          {t('common:action.save')}
        </PrimaryButton>
      </HStack>
    </HStack>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title={t('page:adminSingularDay.modal.editTitle')}
    >
      <VStack align="stretch" gap={4}>
        {errors.root?.serverError !== undefined && (
          <Alert status="error" title={errors.root.serverError.message} />
        )}
        <SingularDayForm
          control={control}
          dateValue={parseDateInputValue(row.dateKey)}
          errors={errors}
          mode="edit"
          onEditGroupNames={onEditGroupNames}
          register={register}
        />
      </VStack>
    </Modal>
  )
}
